import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { ChevronRight, Heart, ArrowRight } from 'lucide-react';
import FiberDetailModal from '../components/FiberDetailModal';

const ResultsScreenCanva = () => {
  const { setCurrentScreen, setCurrentProduct } = useApp();
  const [product, setProduct] = useState(null);
  const [selectedFiber, setSelectedFiber] = useState(null);

  useEffect(() => {
    const savedResult = sessionStorage.getItem('currentScanResult');
    if (savedResult) {
      try {
        const data = JSON.parse(savedResult);
        setProduct(data);
        setCurrentProduct(data);
      } catch (e) {
        console.error('Error loading scan result:', e);
      }
    } else {
      // If no saved result, go back to scan
      setTimeout(() => setCurrentScreen('scan'), 1000);
    }
  }, [setCurrentProduct, setCurrentScreen]);

  // Return early if no product
  if (!product) {
    return (
      <div className="min-h-screen bg-white pb-24 flex items-center justify-center">
        <p className="text-black/55">Loading results...</p>
      </div>
    );
  }

  // Color coding for scores (>= 70 green, 40-69 yellow, < 40 red)
  const getScoreColor = (score) => {
    if (score >= 70) return '#16A34A';
    if (score >= 40) return '#EAB308';
    return '#DC2626';
  };

  // Parse composition string into array
  const parseComposition = () => {
    // Tag scan composition takes priority; fall back to clothing analysis only if it's a simple fiber name
    const ft = (product.clothingAnalysis?.fabric_type || '').trim();
    const isSimpleFiber = /^(cotton|polyester|wool|silk|linen|cashmere|nylon|acrylic|viscose|rayon|elastane|spandex|lyocell|tencel|modal|hemp|bamboo)$/i.test(ft);
    const raw = product.tagScan?.composition
      || product.composition
      || (isSimpleFiber ? ft + ' 100%' : null);
    if (!raw) return [];

    const results = [];
    raw.split(/[,;]/).forEach(part => {
      part = part.trim();
      if (!part) return;

      let name = null;
      let percentage = null;

      // "Cotton 90%", name then number
      let m = part.match(/^([A-Za-z][A-Za-z\s\-/]*?)\s+(\d+(?:\.\d+)?)%?$/);
      if (m) { name = m[1].trim(); percentage = parseFloat(m[2]); }

      // "90% Cotton", number then name
      if (!name) {
        m = part.match(/^(\d+(?:\.\d+)?)%?\s+([A-Za-z].+)$/);
        if (m) { percentage = parseFloat(m[1]); name = m[2].trim(); }
      }

      if (!name || !percentage || isNaN(percentage)) return;

      const isNatural = /cotton|wool|silk|linen|cashmere|hemp|jute|lyocell|tencel|modal|bamboo/i.test(name);
      const isSynthetic = /polyester|nylon|acrylic|elastane|polyamide|spandex|rayon|viscose/i.test(name);
      results.push({
        name,
        percentage,
        type: isNatural ? 'natural' : isSynthetic ? 'synthetic' : 'unknown',
        score: isNatural ? 75 : isSynthetic ? 40 : 60,
      });
    });

    // Deduplicate by fiber name, keep highest % when same name appears from multiple images
    const seen = new Map();
    results.forEach(f => {
      const key = f.name.toLowerCase();
      if (!seen.has(key) || f.percentage > seen.get(key).percentage) seen.set(key, f);
    });
    const deduped = Array.from(seen.values());

    // Normalize to 100% if totals are inflated (multi-image duplication)
    const total = deduped.reduce((s, f) => s + f.percentage, 0);
    if (total > 110) {
      const scale = 100 / total;
      return deduped.map(f => ({ ...f, percentage: Math.round(f.percentage * scale) }));
    }
    return deduped;
  };

  const composition = parseComposition();
  const naturalPercent = composition.filter(f => f.type === 'natural').reduce((sum, f) => sum + f.percentage, 0);
  const syntheticPercent = Math.max(0, composition.filter(f => f.type === "synthetic").reduce((sum, f) => sum + f.percentage, 0));
  const overallScore = Math.round(product.scores?.fabric || 72);
  const scoreColor = getScoreColor(overallScore);
  
  // Get sustainability score separately
  const sustainabilityScore = Math.round(product.scores?.sustainability || 60);
  const getSustainabilityColor = () => getScoreColor(sustainabilityScore);

  return (
    <div className="min-h-screen bg-white pb-24">
      <div className="px-6 pt-6 pb-4 flex items-center gap-4 border-b border-black/10">
        <button
          onClick={() => setCurrentScreen('scan')}
          className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center hover:bg-black/10 transition-all flex-shrink-0"
        >
          <ChevronRight size={18} className="text-black rotate-180" />
        </button>
        <button onClick={() => setActiveTab("home")} className="hover:opacity-80 transition-opacity"><img src="/logos/logo-black.jpg" alt="ATELIA" className="h-16 w-auto object-contain" /></button>
      </div>

      <div className="px-6 pt-6 pb-4">
        <p className="text-xs uppercase tracking-wide text-black/55 mb-2 font-bold">YOUR RESULTS</p>
        <h1 className="text-2xl font-bold text-black uppercase tracking-wide">Analysis Complete</h1>
      </div>

      <div className="px-6 mb-6">
        <div className="aspect-[3/4] rounded-2xl overflow-hidden mb-4 relative">
          <img src={product.imageUrl || product.thumbnailUrl || product.image} alt={product.name} className="w-full h-full object-cover object-center" />
          <button className="absolute top-4 right-4 w-12 h-12 bg-white/95 backdrop-blur rounded-full flex items-center justify-center">
            <Heart size={24} className="text-black" />
          </button>
        </div>
        <p className="text-xs text-black/55 uppercase font-bold mb-1">{product.brand}</p>
        <h2 className="text-xl font-bold text-black mb-2">{product.name}</h2>
        <p className="text-sm text-black/72 mt-2">{product.category}</p>
      </div>

      <div className="px-6 mb-6">
        <p className="text-xs uppercase tracking-wide text-black/55 mb-4 font-bold">Fabric Value Intelligence</p>
        <div className="bg-white border border-black/12 rounded-2xl p-6 text-center">
          <div className="relative w-32 h-32 mx-auto mb-4">
            <svg className="transform -rotate-90" width="128" height="128">
              <circle cx="64" cy="64" r="56" stroke="#00000010" strokeWidth="8" fill="none" />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke={scoreColor}
                strokeWidth="8"
                fill="none"
                strokeDasharray={(overallScore / 100) * 352 + ' 352'}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div>
                <p className="text-4xl font-bold" style={{ color: scoreColor }}>{overallScore}</p>
                <p className="text-xs text-black/55 uppercase">Score</p>
              </div>
            </div>
          </div>
          <p className="text-sm font-bold text-black uppercase">Overall Quality</p>
        </div>
      </div>

      {composition.length > 0 && (
        <div className="px-6 mb-6">
          <p className="text-xs uppercase tracking-wide text-black/55 mb-3 font-bold">Composition Split</p>
          <div className="flex gap-1 h-3 rounded-full overflow-hidden mb-2">
            {naturalPercent > 0 && (
              <div style={{ width: naturalPercent + '%' }} className="bg-green-500" />
            )}
            {syntheticPercent > 0 && (
              <div style={{ width: syntheticPercent + '%' }} className="bg-red-500" />
            )}
          </div>
          <div className="flex justify-between text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full" />
              <span className="font-bold text-black">{naturalPercent}% NATURAL</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-full" />
              <span className="font-bold text-black">{syntheticPercent}% SYNTHETIC</span>
            </div>
          </div>
        </div>
      )}

      <div className="px-6 mb-6">
        <p className="text-xs uppercase tracking-wide text-black/55 mb-3 font-bold">Breakdown All Fibres</p>
        <div className="space-y-2">
          {composition.length > 0 ? (
            composition.map((fiber, i) => (
              <button
                key={i}
                onClick={() => setSelectedFiber(fiber)}
                className="w-full bg-white border border-black/12 rounded-xl p-4 flex items-center justify-between hover:bg-black/5"
              >
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: fiber.type === 'natural' ? '#000' : '#00000050' }} />
                  <div className="text-left">
                    <p className="text-sm font-bold text-black uppercase">{fiber.name}</p>
                    <p className="text-xs text-black/55">{fiber.percentage}% • Score: <span style={{ color: getScoreColor(fiber.score) }} className="font-bold">{fiber.score}/100</span></p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-black/30" />
              </button>
            ))
          ) : (
            <p className="text-sm text-black/55 text-center py-4">No composition data available</p>
          )}
        </div>
      </div>

      <div className="px-6 space-y-3">
        <button
          onClick={() => setCurrentScreen('material-breakdown')}
          className="w-full bg-black text-white py-4 rounded-xl font-bold uppercase flex items-center justify-center gap-2"
        >
          View Sustainability
          <ArrowRight size={18} />
        </button>
        <button
          onClick={() => setCurrentScreen('alternatives-canva')}
          className="w-full bg-white border border-black/12 text-black py-4 rounded-xl font-bold uppercase"
        >
          View Alternatives
        </button>
      </div>

      {/* Fiber Detail Modal */}
      {selectedFiber && (
        <FiberDetailModal 
          fiber={selectedFiber} 
          onClose={() => setSelectedFiber(null)} 
        />
      )}
    </div>
  );
};

export default ResultsScreenCanva;
